import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface TierConfig {
  name: string;
  label: string;
  price: number;
  refreshInterval: number;
  chartTimeframes: string[];
  bollingerBands: boolean;
  volumeProfile: boolean;
  tickerSentenceLimit: number;
  vizierLines: number;
  detailOverlays: string[];
  trackRecord: string;
  aiReasoning: boolean;
  sseAlerts: boolean;
  engineCount: number;
  backtestAccess: boolean;
  coinglassAccess: boolean;
  correlationsAccess: boolean;
  liquidationAccess: boolean;
  dataDelay: number;
}

interface TierContextType {
  tier: string;
  tierConfig: TierConfig | null;
  setTier: (tier: string) => Promise<void>;
  loading: boolean;
}

const TierContext = createContext<TierContextType>({
  tier: 'premium',
  tierConfig: null,
  setTier: async () => {},
  loading: true,
});

export function TierProvider({ children }: { children: React.ReactNode }) {
  const [tier, setTierState] = useState('premium');
  const [tierConfig, setTierConfig] = useState<TierConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/tier/current')
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setTierState(data.tier);
          setTierConfig(data.config);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setTier = useCallback(async (newTier: string) => {
    try {
      const res = await fetch('/api/tier/set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier: newTier }),
      });
      const data = await res.json();
      if (data.success) {
        setTierState(data.tier);
        setTierConfig(data.config);
      }
    } catch (e) {}
  }, []);

  return <TierContext.Provider value={{ tier, tierConfig, setTier, loading }}>{children}</TierContext.Provider>;
}

export function useTier() {
  return useContext(TierContext);
}
